import dayjs from 'dayjs'
import { type KeycloakTokenParsed } from 'keycloak-js'
import { remove } from 'es-cookie'
import { extractTopDomainFromHostname } from './CookieUtils'

type Token = KeycloakTokenParsed & CustomerPortalTokenAttributes

interface CustomerPortalTokenAttributes {
  partnerProxyId: string
  accountId: string
  idLevel: number
  permissions: string | undefined
  preferred_username?: string
}

export async function getToken(): Promise<string | undefined> {
  await globalThis.keycloak.updateToken(minValidityInSeconds)
  return globalThis.keycloak.token
}

function getTokenParsed(): Token | undefined {
  return globalThis.keycloak.tokenParsed as Token
}

function getRefreshTokenParsed(): Token | undefined {
  return globalThis.keycloak.refreshTokenParsed as Token
}

export function getEmail(): string | null {
  const token = getTokenParsed()
  if (token !== undefined) {
    return token.email
  }
  return null
}

export function getPreferredUsername(): string | null {
  const token = getTokenParsed()
  if (token !== undefined) {
    return token.preferred_username ?? null
  }
  return null
}

export async function renewTokenWithRedirect(): Promise<void> {
  await globalThis.keycloak
    .updateToken(2 * 60 * 60)
    .then((_isRefreshed) => {
      // everything is fine, token is refreshed or still valid
    })
    .catch(() => {
      console.log('update token not successful')
      keycloakLogout(globalThis.ENV_CONFIG?.REDIRECTS.LOGOUT_REDIRECT)
    })
}

export function getPartnerProxyId(): string | null {
  const token = getTokenParsed()
  if (token !== undefined) {
    return token.partnerProxyId
  }
  return null
}

export function getIdLevel(): number | null {
  const token = getTokenParsed()
  if (token !== undefined) {
    return token.idLevel
  }
  return null
}

export function getMigrated(): string | null {
  const token = getTokenParsed()
  if (token !== undefined) {
    return token.migrated
  }
  return null
}

export function getAccountId(): string | null {
  const token = getTokenParsed()
  if (token !== undefined) {
    return token.accountId
  }
  return null
}

export function isAuthenticated(): boolean {
  if (globalThis.keycloak?.authenticated !== undefined) {
    return globalThis.keycloak.authenticated
  }
  return false
}

export function hasPermission(permission: string): boolean {
  const token = getTokenParsed()
  if (token?.permissions) {
    const found = token.permissions.split(' ').find((it) => it == permission)
    if (found !== undefined) return true
  }
  return false
}

export function hasRole(role: string): boolean {
  const token = getTokenParsed()
  return token?.realm_access?.roles.includes(role) ?? false
}

// enforce update of token
// if token validity is under one day --> update
export async function renewToken(): Promise<string | undefined> {
  const oneDay = 60 * 60 * 24
  await globalThis.keycloak.updateToken(oneDay)
  return globalThis.keycloak.token
}

export function getRefreshTokenExpireInSeconds(now: number): number {
  const token = getRefreshTokenParsed()
  if (token?.exp !== undefined) {
    return dayjs(token.exp).diff(dayjs(Math.floor(now / 1000)))
  }
  return -1
}

export function getDateStringFromSeconds(seconds: number): string {
  return new Date(seconds * 1000).toISOString().substring(11, 19)
}

export async function keycloakLogout(redirectUri?: string): Promise<void> {
  const domain: string = '.' + extractTopDomainFromHostname()
  remove('access_token', { domain })
  remove('refresh_token', { domain })
  remove('cp_login', { domain })
  remove('isRedCooperationPartner', { domain })

  globalThis.dispatchEvent(
    new CustomEvent('cp_logout_successful', {
      bubbles: true,
      detail: {}
    })
  )

  await globalThis.keycloak.logout({
    ...(redirectUri && { redirectUri })
  })
}

export const minValidityInSeconds = 30
export const tokenValidityInSeconds = 2 * 60 * 60
